import type React from 'react';

/** Rectangle describing the spotlight target position. */
export interface SpotlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Calculates tooltip position relative to the spotlight target.
 * Returns CSS properties for absolute positioning.
 */
export function calculateTooltipPosition(
  rect: SpotlightRect | null,
  position: 'top' | 'bottom' | 'left' | 'right' | 'center',
  tooltipWidth: number = 360,
  gap: number = 16,
): React.CSSProperties {
  if (!rect) {
    return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const style: React.CSSProperties = { position: 'fixed' };

  switch (position) {
    case 'bottom': {
      style.top = rect.y + rect.height + gap;
      style.left = Math.max(16, Math.min(rect.x + rect.width / 2 - tooltipWidth / 2, vw - tooltipWidth - 16));
      break;
    }
    case 'top': {
      style.bottom = vh - rect.y + gap;
      style.left = Math.max(16, Math.min(rect.x + rect.width / 2 - tooltipWidth / 2, vw - tooltipWidth - 16));
      break;
    }
    case 'right': {
      const estimatedTooltipHeight = 220;
      const topR = Math.max(16, rect.y + rect.height / 2 - 80);
      style.top = Math.min(topR, vh - estimatedTooltipHeight - 16);
      style.left = Math.min(rect.x + rect.width + gap, vw - tooltipWidth - 16);
      break;
    }
    case 'left': {
      const estimatedTooltipHeight = 220;
      const topL = Math.max(16, rect.y + rect.height / 2 - 80);
      style.top = Math.min(topL, vh - estimatedTooltipHeight - 16);
      style.right = vw - rect.x + gap;
      break;
    }
    case 'center':
    default:
      style.top = '50%';
      style.left = '50%';
      style.transform = 'translate(-50%, -50%)';
  }

  return style;
}
